/**
 * Dogs Hooks
 *
 * TanStack Query hooks for dogs CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dogsApi } from '@/shared/lib/api/dogs-api';
import type {
  CreateDogRequest,
  UpdateDogRequest,
} from '@/shared/types/api.types';

// ===========================
// Query Hooks
// ===========================

/**
 * Get all dogs for the authenticated user
 */
export function useDogs() {
  return useQuery({
    queryKey: ['dogs'],
    queryFn: dogsApi.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get a single dog by ID
 */
export function useDog(dogId: string | undefined) {
  return useQuery({
    queryKey: ['dogs', dogId],
    queryFn: () => {
      if (!dogId) throw new Error('Dog ID is required');
      return dogsApi.getById(dogId);
    },
    enabled: !!dogId, // Only fetch if dogId is provided
    staleTime: 5 * 60 * 1000,
  });
}

// ===========================
// Mutation Hooks
// ===========================

/**
 * Create a new dog
 * Returns { dogId: string } per API_GUIDE.md
 */
export function useCreateDog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDogRequest) => dogsApi.create(data),
    onSuccess: () => {
      // Invalidate dogs list to refetch with new dog
      void queryClient.invalidateQueries({ queryKey: ['dogs'] });
    },
  });
}

/**
 * Update an existing dog
 * API returns 204 No Content per API_GUIDE.md
 */
export function useUpdateDog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dogId, data }: { dogId: string; data: UpdateDogRequest }) =>
      dogsApi.update(dogId, data),
    onSuccess: (_data, variables) => {
      // Refetch to get updated data from server
      void queryClient.invalidateQueries({ queryKey: ['dogs', variables.dogId] });
      void queryClient.invalidateQueries({ queryKey: ['dogs'] });
    },
  });
}

/**
 * Delete a dog (soft delete)
 */
export function useDeleteDog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dogId: string) => dogsApi.delete(dogId),
    onSuccess: () => {
      // Invalidate dogs list
      void queryClient.invalidateQueries({ queryKey: ['dogs'] });
    },
  });
}
